import type { SponsorCampaign } from "./campaignTypes";

const SESSION_KEY_PREFIX = "cuo_partner_session:";

export function partnerSessionKey(slug: string): string {
  return `${SESSION_KEY_PREFIX}${slug}`;
}

export function getStoredPartnerSession(slug: string): string | null {
  try {
    return sessionStorage.getItem(partnerSessionKey(slug));
  } catch {
    return null;
  }
}

export function storePartnerSession(slug: string, token: string): void {
  try {
    sessionStorage.setItem(partnerSessionKey(slug), token);
  } catch {
    // ignore quota / private mode
  }
}

export function clearPartnerSession(slug: string): void {
  try {
    sessionStorage.removeItem(partnerSessionKey(slug));
  } catch {
    // ignore
  }
}

export async function requestPartnerAccess(email: string, slug: string): Promise<{
  ok: boolean;
  message?: string;
  error?: string;
  debugMagicUrl?: string;
  debugEmailSkipped?: boolean;
}> {
  let res: Response;
  try {
    res = await fetch(`${import.meta.env.VITE_API_BASE_URL}partners/auth/request/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, slug }),
    });
  } catch {
    return {
      ok: false,
      error: "Could not reach the partner API. Try again in a moment.",
    };
  }
  let data: Record<string, unknown> = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  if (!res.ok) {
    const fallback =
      res.status >= 500
        ? "Could not reach the partner API. Try again in a moment."
        : "Could not send access email.";
    return { ok: false, error: (data.error as string) || fallback };
  }
  return {
    ok: true,
    message: data.message as string | undefined,
    debugMagicUrl: data.debug_magic_url as string | undefined,
    debugEmailSkipped: Boolean(data.debug_email_skipped),
  };
}

export async function verifyPartnerMagicToken(token: string): Promise<{
  ok: boolean;
  sessionToken?: string;
  email?: string;
  slug?: string;
  error?: string;
}> {
  let res: Response;
  try {
    res = await fetch(`${import.meta.env.VITE_API_BASE_URL}partners/auth/verify/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
  } catch {
    return { ok: false, error: "Could not reach the API." };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data.error || "Invalid or expired access link." };
  }
  return {
    ok: true,
    sessionToken: data.session_token,
    email: data.email,
    slug: data.slug,
  };
}

export async function fetchPartnerCampaign(
  slug: string,
  sessionToken?: string | null
): Promise<{ ok: true; campaign: SponsorCampaign } | { ok: false; error: string; status: number }> {
  const headers: Record<string, string> = {};
  if (sessionToken) {
    headers.Authorization = `Bearer ${sessionToken}`;
  }
  let res: Response;
  try {
    res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}partners/${encodeURIComponent(slug)}/`,
      { headers }
    );
  } catch {
    return {
      ok: false,
      error: "Could not reach the API.",
      status: 0,
    };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      ok: false,
      error: data.error || "Could not load campaign.",
      status: res.status,
    };
  }
  return { ok: true, campaign: data as SponsorCampaign };
}
